import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import StepForm from '../../../../../features/components/Forms/StepForm'

import useHandleFormik from './hooks/useHandleFormik'
import useLineItemFormSteps from './hooks/useLineItemFormSteps'

import { LineItemFormContext } from './LineItemFormContextProvider'

import { getObjectStoreUrl } from '../helpers'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { clearCreateLineItem } from '../../../../../modules/actions/lineItems'
import { getChoicesCountries } from '../../../../../modules/actions/choices'

import {
  lineItemCreatedDataSelector,
  lineItemCreateErrorSelector,
  lineItemCreateIsLoadingSelector,
  lineItemsSelector,
  lineItemWasCreatedSelector
} from '../../../../../modules/selectors/lineItems'
import { facebookRightUploadAdCampaignSelector } from '../../../../../modules/selectors/rights'
import { selfAccountIsLoadingSelector } from '../../../../../modules/selectors/selfAccounts'
import { choicesCountriesWasLoadedSelector, facebookClientAppsSelector } from '../../../../../modules/selectors/choices'

import { FACEBOOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { OUTCOME_APP_PROMOTION, OUTCOME_TRAFFIC } from '../../../../../constants/campaigns'
import { LINE_ITEM } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import { OBJECTIVE } from '../../../../Multiplatform/CampaignForms/fields'
import { PROMOTED_PIXEL } from '../../../../../constants/selectLists/customEventTypes/fields'
import { ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS, OBJECT_STORE_URL, USER_OS } from './fields'
import { LINE_ITEM_FORM } from '../index'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import { APPLICATION_ID } from '../reusableFields/AppListField/fields'
import { campaignSelector } from '../../../../../modules/selectors/campaigns'

const LineItemForm = ({ formProgressItems, onSuccessSubmit, selectedCampaignObjective }) => {
  const dispatch = useDispatch()

  const [successSubmit, setSuccessSubmit] = useState(false)

  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const lineItems = useSelector(lineItemsSelector)
  const lineItemFacebookCreatedData = useSelector(lineItemCreatedDataSelector)
  const lineItemWasCreated = useSelector(lineItemWasCreatedSelector)
  // self account is fetched after switching from the campaign form to line item
  const selfAccountIsLoading = useSelector(selfAccountIsLoadingSelector)
  const countriesWasLoaded = useSelector(choicesCountriesWasLoadedSelector)
  const campaign = useSelector(facebookRightUploadAdCampaignSelector)
  const campaignDetails = useSelector(campaignSelector)
  const facebookClientApps = useSelector(facebookClientAppsSelector)

  const {
    isDataLoading,
    isIos14Campaign,
    preselectedValues,
    showPromotedObjectStepForTrafficCampaign,
    showPromotedObjectStepForAppPromotionCampaign
  } = useContext(LineItemFormContext)

  const formik = useHandleFormik({
    preselectedValues,
    selectedCampaignObjective,
    showPromotedObjectStepForAppPromotionCampaign,
    setSuccessSubmit
  })
  const { values } = formik

  const steps = useLineItemFormSteps({
    values,
    showPromotedObjectStepForTrafficCampaign,
    showPromotedObjectStepForAppPromotionCampaign
  })

  const isInitialDataLoading = useMemo(() => selfAccountIsLoading, [selfAccountIsLoading])

  const onSuccessSubmitHandler = useCallback(() => {
    if (campaign[OBJECTIVE] === OUTCOME_TRAFFIC) {
      // for traffic campaigns we handle pixel selection on lineItem form
      // but use it in ad form
      onSuccessSubmit({
        [ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS]: values[ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS],
        [PROMOTED_PIXEL]: values[PROMOTED_PIXEL]
      })
    } else if (campaign[OBJECTIVE] === OUTCOME_APP_PROMOTION) {
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedLineItemData = lineItems.find(lineItem => values[LINE_ITEM] === lineItem.id)
        const promotedObject = selectedLineItemData.promoted_object
        onSuccessSubmit({
          [APPLICATION_ID]: promotedObject[APPLICATION_ID],
          [OBJECT_STORE_URL]: promotedObject[OBJECT_STORE_URL]
        })
      } else {
        if (isIos14Campaign) {
          // if campaign is ios14 - we don't show promoted object step - so we take data from campaign
          onSuccessSubmit({
            [APPLICATION_ID]: campaignDetails?.promoted_object?.[APPLICATION_ID],
            [OBJECT_STORE_URL]: campaignDetails?.promoted_object?.[OBJECT_STORE_URL]
          })
        } else {
          onSuccessSubmit({
            [APPLICATION_ID]: values[APPLICATION_ID],
            [OBJECT_STORE_URL]: getObjectStoreUrl({
              facebookClientApps,
              applicationId: values[APPLICATION_ID],
              platform: values[USER_OS]
            })
          })
        }
      }
    } else {
      onSuccessSubmit()
    }
  }, [campaign, onSuccessSubmit, values, lineItems, facebookClientApps, campaignDetails, isIos14Campaign])

  const clearCreateLineItemHandler = useCallback(() => {
    dispatch(clearCreateLineItem())
  }, [dispatch])

  useEffect(() => {
    // getChoicesCountries for payload creation
    if (!countriesWasLoaded) {
      dispatch(getChoicesCountries())
    }
  }, [dispatch, countriesWasLoaded])

  useEffect(() => {
    if (lineItemWasCreated) {
      dispatch(setRightUploadAdData({ lineItem: lineItemFacebookCreatedData }, FACEBOOK_PLATFORM))

      setSuccessSubmit(true)
    }
  }, [dispatch, lineItemWasCreated, lineItemFacebookCreatedData])

  return (
    <StepForm
      steps={steps}
      formik={formik}
      formName={LINE_ITEM_FORM}
      formProgressItems={formProgressItems}
      submitText="Save and continue"
      onSuccessSubmit={onSuccessSubmitHandler}
      clearSubmitHandler={clearCreateLineItemHandler}
      successSubmit={successSubmit}
      isInitialDataLoading={isInitialDataLoading || isDataLoading}
      errorSelector={lineItemCreateErrorSelector}
      isLoadingSelector={lineItemCreateIsLoadingSelector}
    />
  )
}

export default LineItemForm
